import React from 'react'
import { Badge, Button, Box, Tooltip, Icon } from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import { detectConfigVersion, getVersionLabel } from '../utils/configVersion'

interface ConfigVersionBadgeProps {
  config: TranslatorConfigConfig
  showUpgradeButton?: boolean
  onUpgrade?: () => void
  isUpgrading?: boolean
}

export const ConfigVersionBadge: React.FC<ConfigVersionBadgeProps> = ({
  config,
  showUpgradeButton = false,
  onUpgrade,
  isUpgrading = false,
}) => {
  const version = detectConfigVersion(config)
  const isV1 = version === '1'

  const upgradeTooltipContent = (
    <Box fontSize="sm">
      <Box fontWeight="bold" mb={2}>
        Upgrade to Version 2
      </Box>
      <Box mb={2}>
        This will convert your configuration to Version 2 format with the following benefits:
      </Box>
      <Box as="ul" pl={4} mb={2}>
        <li>Group multiple schedules that share the same adapter and configuration</li>
        <li>Easier to manage related schedules (e.g., regular run + scrubber)</li>
        <li>Reduced duplication of configuration settings</li>
        <li>Supports both normal and scrubber schedules in one group</li>
      </Box>
      <Box fontStyle="italic">
        The upgrade will NOT be saved automatically. You'll be able to review the changes before
        saving.
      </Box>
    </Box>
  )

  return (
    <Box display="inline-flex" alignItems="center" gap={2}>
      <Badge colorScheme={isV1 ? 'yellow' : 'blue'} fontSize="sm" px={3} py={1} borderRadius="md">
        {getVersionLabel(version)}
      </Badge>

      {isV1 && showUpgradeButton && onUpgrade && (
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Button
            size="xs"
            colorScheme="blue"
            onClick={onUpgrade}
            isLoading={isUpgrading}
            loadingText="Upgrading..."
          >
            Upgrade to V2
          </Button>
          <Tooltip label={upgradeTooltipContent} fontSize="sm" placement="bottom-start" hasArrow>
            <Icon as={InfoIcon} color="blue.500" cursor="help" boxSize={4} />
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}
